/**
 * Public analytics API — the ONLY module other code should import from.
 *
 * Exported surface:
 *   track(event, props?)          — fire a Mixpanel event (no-op when unconfigured)
 *   identify(userId, traits?)     — identify a user in Mixpanel (no-op when unconfigured)
 *   resetAnalytics()              — reset Mixpanel identity on logout
 *   captureError(err, ctx?)       — send an exception to Sentry (no-op when unconfigured)
 *   usePageTracking               — hook: fires page_view on every route change
 *   EVENTS                        — canonical event name constants
 *   SentryErrorBoundary           — React error boundary (wraps @sentry/react or plain fallback)
 *   initTelemetry()               — initialise both Sentry and Mixpanel (call once before render)
 */
export { EVENTS } from "./events";
export type { EventName } from "./events";

export { mpTrack as _mpTrack, mpReset as _mpReset } from "./mixpanel";
export { SentryErrorBoundary, captureError } from "./sentry";
export { usePageTracking } from "./usePageTracking";

import { initMixpanel, mpTrack, mpIdentify, mpReset, mpSetUserProps } from "./mixpanel";
import { initSentry } from "./sentry";

/** Initialise Sentry and Mixpanel.  Call once before rendering the root component. */
export function initTelemetry(): void {
  initSentry();
  initMixpanel();
}

/**
 * Track an analytics event.
 * Routes to Mixpanel (and is silent when unconfigured so dev/tests never phone home).
 */
export function track(event: string, props?: Record<string, unknown>): void {
  mpTrack(event, props);
}

/**
 * Identify a user in the analytics system (call on login / token restore).
 * Passes traits (org_id, role, plan, …) as Mixpanel People properties.
 */
export function identify(userId: string, traits?: Record<string, unknown>): void {
  mpIdentify(userId, traits);
}

/**
 * Reset analytics identity (call on logout so the next anonymous session starts fresh).
 */
export function resetAnalytics(): void {
  mpReset();
}

// Re-export mpSetUserProps for internal use (e.g. updating plan after upgrade).
export { mpSetUserProps as setUserProps };
